/**
 * Facebook Cookie Management Utility
 * Handles fbclid parameter extraction and _fbc cookie creation
 * Following Meta's Conversions API specifications
 */

/**
 * Get subdomain index for _fbc cookie format
 * @param hostname - The current hostname
 * @returns Subdomain index (0 for localhost/no subdomain, 1 for domain, 2 for subdomain.domain)
 */
function getSubdomainIndex(hostname: string): number {
  // localhost or IP addresses get index 0
  if (hostname === 'localhost' || /^(\d{1,3}\.){3}\d{1,3}$/.test(hostname)) {
    return 0
  }

  // Count the dots in the hostname
  const parts = hostname.split('.')

  // Single word domains (like 'com') get 0
  if (parts.length === 1) {
    return 0
  }

  // domain.com gets 1
  if (parts.length === 2) {
    return 1
  }

  // subdomain.domain.com or www.domain.com gets 2
  return 2
}

/**
 * Extract fbclid from URL parameters
 * @param url - The URL to parse (defaults to current window location)
 * @returns The fbclid value if present, null otherwise
 */
export function extractFbclid(url?: string): string | null {
  const urlToParse = url || window.location.href
  const urlObj = new URL(urlToParse)
  const fbclid = urlObj.searchParams.get('fbclid')

  // fbclid is case-sensitive, so we preserve the exact value
  return fbclid
}

/**
 * Create or update _fbc cookie with proper format
 * @param fbclid - The fbclid value from URL
 * @param hostname - Optional hostname for testing
 * @returns The formatted _fbc value
 */
export function createFbcCookie(fbclid: string, hostname?: string): string {
  const host = hostname || window.location.hostname
  const subdomainIndex = getSubdomainIndex(host)
  const timestamp = Date.now()

  // Format: fb.{subdomainIndex}.{creationTimeMs}.{fbclid}
  const fbcValue = `fb.${subdomainIndex}.${timestamp}.${fbclid}`

  // Set cookie with 90-day expiration
  const expirationDate = new Date()
  expirationDate.setDate(expirationDate.getDate() + 90)

  // Set cookie with proper parameters
  // Using SameSite=Lax for compatibility with payment redirects
  document.cookie = `_fbc=${fbcValue}; expires=${expirationDate.toUTCString()}; path=/; SameSite=Lax`

  return fbcValue
}

/**
 * Get existing _fbc cookie value
 * @returns The _fbc cookie value if present, null otherwise
 */
export function getFbcCookie(): string | null {
  const cookies = document.cookie.split(';').reduce((acc, cookie) => {
    const [key, value] = cookie.trim().split('=')
    acc[key] = value
    return acc
  }, {} as Record<string, string>)

  return cookies['_fbc'] || null
}

/**
 * Get existing _fbp cookie value
 * @returns The _fbp cookie value if present, null otherwise
 */
export function getFbpCookie(): string | null {
  const cookies = document.cookie.split(';').reduce((acc, cookie) => {
    const [key, value] = cookie.trim().split('=')
    acc[key] = value
    return acc
  }, {} as Record<string, string>)

  return cookies['_fbp'] || null
}

/**
 * Extract fbclid from existing _fbc cookie
 * @param fbcValue - The _fbc cookie value
 * @returns The fbclid portion if valid format, null otherwise
 */
export function extractFbclidFromCookie(fbcValue: string): string | null {
  if (!fbcValue || !fbcValue.startsWith('fb.')) {
    return null
  }

  const parts = fbcValue.split('.')
  if (parts.length < 4) {
    return null
  }

  // fbclid is everything after the third dot
  return parts.slice(3).join('.')
}

/**
 * Process fbclid from URL and manage _fbc cookie
 * This is the main function to call on page load or before checkout
 * @returns Object with fbc and fbp cookie values
 */
export function processFacebookCookies(): { fbc: string | null; fbp: string | null } {
  // Check for fbclid in URL
  const fbclid = extractFbclid()

  // Get existing _fbc cookie
  const existingFbc = getFbcCookie()

  let fbc = existingFbc

  if (fbclid) {
    // If we have fbclid in URL, check if we need to update the cookie
    if (!existingFbc) {
      // No existing cookie, create new one
      fbc = createFbcCookie(fbclid)
    } else {
      // Cookie exists, check if fbclid is different
      const existingFbclid = extractFbclidFromCookie(existingFbc)

      if (existingFbclid !== fbclid) {
        // Different fbclid, update the cookie
        fbc = createFbcCookie(fbclid)
      }
    }
  }

  // Also get _fbp cookie
  const fbp = getFbpCookie()

  return { fbc, fbp }
}

/**
 * Validate _fbc cookie format (matches backend validation)
 * @param fbc - The _fbc cookie value to validate
 * @returns true if valid format, false otherwise
 */
export function validateFbcFormat(fbc: string): boolean {
  if (!fbc || !fbc.startsWith('fb.')) {
    return false
  }

  const parts = fbc.split('.')
  if (parts.length < 4) {
    return false
  }

  // Check subdomain index is numeric
  const subdomainIndex = parseInt(parts[1], 10)
  if (isNaN(subdomainIndex)) {
    return false
  }

  // Check timestamp is numeric and reasonable
  const timestamp = parseInt(parts[2], 10)
  if (isNaN(timestamp) || timestamp < 1577836800000 || timestamp > Date.now() + 86400000) {
    return false
  }

  // Check fbclid has valid characters
  const fbclid = parts.slice(3).join('.')
  if (!/^[A-Za-z0-9_-]+$/.test(fbclid)) {
    return false
  }

  return true
}

/**
 * Initialize Facebook Pixel with user email
 * @param email - User's email address (unhashed)
 */
export function initializeFacebookPixelWithEmail(email: string): void {
  if (typeof (window as any).fbq !== 'undefined') {
    (window as any).fbq('init', '1457213788661203', {
      em: email
    });
    console.log('🎯 Facebook Pixel re-initialized with user email');
  }
}